import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { TableDataResponse } from 'types/general';
import { BenefitCategory } from 'store/benefits';
import { IBaseBenefitTemplate, IBenefitTemplate } from 'types/income';
import { getSorting } from 'utils/general';
import { Translations } from 'types/Common';
import { BenefitTemplateStatus } from 'store/benefit-templates';
import { AdminBenefitTemplatesFilters } from './admin-benefit-templates.types';

interface GetBenefitTemplates {
  filters: Partial<AdminBenefitTemplatesFilters>;
}

interface GetBenefitTemplate {
  benefitTemplateId: string;
}

interface CreateBenefitTemplate {
  name: string;
  category: BenefitCategory;
}

interface UpdateBenefitTemplate {
  benefitTemplateId: string;
  data: Partial<
    CreateBenefitTemplate & {
      status: BenefitTemplateStatus;
      thumbnailId: string | null;
      supportedCountries: string[];
      translations: Translations<{
        title: string;
        description: string;
        imageId: string | null;
        attachments: Array<{ fileId: string }>;
        links: Array<{ name: string; url: string; index: number }>;
        faq: Array<{ question: string; answer: string; index: number }>;
      }>;
    }
  >;
}

interface CopyBenefitTemplate {
  benefitTemplateId: string;
}

interface DeleteBenefitTemplate {
  benefitTemplateId: string;
}

export const loaders = {
  getBenefitTemplates: ({ filters }: GetBenefitTemplates) => {
    return api<TableDataResponse<IBaseBenefitTemplate>>({
      url: Endpoints.benefitTemplates.root,
      version: 'v2',
      context: 'melp',
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        ...getSorting(filters.sort),
        search: filters.search || undefined,
        filter: {
          type: filters.category,
          status: filters.status,
          fromModificationDate: filters.date?.from,
          toModificationDate: filters.date?.to,
        },
      },
    });
  },
  getBenefitTemplate: ({ benefitTemplateId }: GetBenefitTemplate) => {
    return api<IBenefitTemplate>({
      url: Endpoints.benefitTemplates.byId.root.replace(
        ':benefitTemplateId',
        benefitTemplateId,
      ),
      context: 'melp',
    });
  },
  createBenefitTemplate: ({ name, category }: CreateBenefitTemplate) => {
    return api<{ id: string }>({
      url: Endpoints.benefitTemplates.create,
      context: 'melp',
      data: {
        name,
        type: category,
      },
      method: 'post',
    });
  },
  updateBenefitTemplate: ({
    benefitTemplateId,
    data: { category, ...data },
  }: UpdateBenefitTemplate) => {
    return api<{ id: string }>({
      url: Endpoints.benefitTemplates.byId.root.replace(
        ':benefitTemplateId',
        benefitTemplateId,
      ),
      context: 'melp',
      data: {
        type: category,
        ...data,
      },
      method: 'patch',
    });
  },
  copyBenefitTemplate: ({ benefitTemplateId }: CopyBenefitTemplate) => {
    return api<{ id: string }>({
      url: Endpoints.benefitTemplates.byId.copy.replace(
        ':benefitTemplateId',
        benefitTemplateId,
      ),
      context: 'melp',
      method: 'post',
    });
  },
  deleteBenefitTemplate: ({ benefitTemplateId }: DeleteBenefitTemplate) => {
    return api({
      url: Endpoints.benefitTemplates.byId.root.replace(
        ':benefitTemplateId',
        benefitTemplateId,
      ),
      context: 'melp',
      method: 'delete',
    });
  },
};
